import React, { ReactElement } from 'react';
import { DefaultFieldRenderer } from './field-renderers';
import { FieldFactory, FieldProps, FieldRenderer, FieldRendererProps, FieldType, SelectOption } from './types';
import noop from 'lodash/noop';

/**
 * Generate a selector to get the appropriate error value for a field from a field error manifest.
 *
 * @todo This function was written before React hooks. A modern version of error handling would not work like this.
 *
 * @example getFieldError(null)('email'); // false
 * @example getFieldError(['phone'])('email'); // false
 * @example getFieldError(['phone', 'email'])('email'); // true
 * @example getFieldError({phone: false})('email'); // false
 * @example getFieldError({phone: false, email: true})('email'); // true
 * @example getFieldError({phone: null, email: 'Invalid email!'})('email'); // 'Invalid email!'
 */
export const getFieldError: {
  (error: null): (field: string) => false;
  (error: string[]): (field: string) => boolean;
  <T>(error: { [k: string]: T }): (field: string) => T;
} = (error) => (field) => !error ? false : error instanceof Array ? error.includes(field) : error[field];

export { noop };

/**
 * Create a stateless functional field type based on a factory function.
 *
 * The factory function will receive props after being processed by the
 * renderer, and should return a JSX element for the field.
 *
 * You are allowed to pass a factory that takes one generic type to
 * specify the type of the field value.
 *
 * Use this to create stateless functional fields that don't have any special
 * requirements for how to use the renderer. For fields that need to do anything
 * other than pass all props directly to the renderer unmodified or have content
 * that is not contained within the renderer, you will need to build the field
 * directly and use the renderer explicitly.
 *
 * @param factory Field factory function
 * @param DefaultFieldRenderer Renderer used when `Renderer` property is absent
 */
export const createField =
  <Value, ElementType extends HTMLElement, FFProps extends object = object>(
    factory: FieldFactory<Value, ElementType, FFProps>,
    DefaultRenderer = DefaultFieldRenderer
  ): FieldType<Value, ElementType, FFProps> =>
  (props: FieldProps<Value, ElementType> & FFProps) => {
    const Renderer = props.Renderer || DefaultRenderer;
    return <Renderer {...props} factory={factory} />;
  };

export const createFieldRenderer = (factory: (props: FieldRendererProps) => ReactElement<any>): FieldRenderer =>
  factory;

export function selectOptionsToTags(selectOptions: SelectOption[]) {
  return selectOptions.map(({ label, value }) => ({
    id: value,
    name: label,
  }));
}
